import React, { type ChangeEvent, type FC, type MouseEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import { type FormikContextType } from "formik";
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconAdd from "@mui/icons-material/Add";
import { type ApolloCache, type DefaultContext, type MutationFunctionOptions } from "@apollo/client";
import omit from "lodash/omit";
import isEmpty from "lodash/isEmpty";
import { useSnackbar } from "notistack";

import { getPristineEmergencyContact } from "../../hooks/useEmergencyContacts";
import { EmergencyContactForm } from "../EmergencyContactForm/EmergencyContactForm";
import { Button } from "components/Shared/Inputs";
import { type IActivityDetails, type IEmergencyContact } from "components/CaseManagement/interfaces";
import { type ClientEmergencyContactVars, type UpdateEmergencyContactsResponse } from "../../queries";
import { STATUS_TYPES } from "components/CaseManagement/constants";
import { DeleteContactModal } from "../EmergencyContactDialogs";
import { useTranslation } from "react-i18next";

interface EmergencyContactFormListProps {
    activity: IActivityDetails;
    onSubmit: (options?: MutationFunctionOptions<UpdateEmergencyContactsResponse, ClientEmergencyContactVars, DefaultContext, ApolloCache<any>>) => Promise<any>;
    onCancel?: () => void;
    afterSubmit?: () => void;
    readonly?: boolean;
    busy?: boolean;
    viewMode?: 'form'|'tab'
}

export const EmergencyContactFormList: FC<EmergencyContactFormListProps> = React.memo(({ activity, onSubmit, onCancel, readonly, busy, viewMode = 'form' }) => {
    const MAX_CONTACTS = 3;
    const DELETE_ALL_INDEX = -1;
    const [emergencyContacts, setEmergencyContacts] = useState(
        activity?.client?.clientEmergencyContacts.length > 0 ? 
        activity.client.clientEmergencyContacts.map(contact => omit(contact, ['__typename']) as IEmergencyContact) : 
        [getPristineEmergencyContact()]
    );
    const { t } = useTranslation("emergencyContact", { useSuspense: false });
    const [toBeDeleted, setToBeDeleted] = useState<IEmergencyContact[]>([]);
    const [form1, setForm1] = useState<FormikContextType<IEmergencyContact>|null>(null);
    const [form2, setForm2] = useState<FormikContextType<IEmergencyContact>|null>(null);
    const [form3, setForm3] = useState<FormikContextType<IEmergencyContact>|null>(null);
    const [contactDeclined, setContactDeclined] = useState<boolean|undefined>(activity?.client?.contactAddress?.emergencyContactDeclined);
    const [uniqueKeys, setUniqueKeys] = useState<number[]>([Date.now()]);
    const [deleteIndex, setDeleteIndex] = useState<number|null>(null);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        for(let i = 1; i < emergencyContacts.length; i++) {
            setUniqueKeys([...uniqueKeys, Date.now()]);
        }
        return () => setUniqueKeys([]);
    }, []);

    // keep emergency contact list in sync with formik values
    useEffect(() => {
        let updatedContacts = [...emergencyContacts];
        getActiveForms().forEach((form, i) => {
            updatedContacts[i] = { ...updatedContacts[i], ...(form?.values ?? {}) };
        });
        setEmergencyContacts(updatedContacts);
        // console.info("🤒🔁🥞 contactDeclined, emergencyContacts:", contactDeclined, emergencyContacts, formikGetters.filter(f => !isEmpty(f?.errors)).map(f => f?.errors));
    }, [form1?.values, form2?.values, form3?.values]);

    const formikGetters = [
        form1,
        form2,
        form3,
    ];

    const formikSetters = [
        setForm1,
        setForm2,
        setForm3
    ];
    const labelSuffix = {
        0: `(${t('primary')})`,
        1: `(${t('secondary')})`,
        2: `(${t('tertiary')})`
    }

    const getActiveForms = () => formikGetters.filter(Boolean);

    function isPristine(values: IEmergencyContact) {
        const pristineContactForm = getPristineEmergencyContact();
        return !values || Object.keys(omit(pristineContactForm, ['status'])).every(key => !values[key]);
    }

    function allValid() {
        if (getActiveForms().length === 0) return false;
        return getActiveForms().every(form => form?.isValid && !isPristine(form.values));
    }

    function addMore() {
        if (emergencyContacts?.length < MAX_CONTACTS) {
            setEmergencyContacts([
                ...(getActiveForms().map((form, i) => ({ ...(emergencyContacts?.[i] ?? {}), ...form?.values })) || []), 
                getPristineEmergencyContact()
            ]);
            setContactDeclined(undefined);
            setUniqueKeys([...uniqueKeys, Date.now()]);
        }
    }

    const remove = useCallback((index: number) => {
        let newContacts: IEmergencyContact[];
        let forDeletion: IEmergencyContact[];
        if (index === DELETE_ALL_INDEX) {
            newContacts = emergencyContacts
                            .filter(contact => !!contact?.clientEmergencyContactId)
                            .map(contact => ({ ...contact, status: STATUS_TYPES.INACTIVE }));
            setEmergencyContacts([]);
            forDeletion = [...toBeDeleted, ...newContacts];
            setToBeDeleted(forDeletion);
            setContactDeclined(true);
            handleDeleteAll(forDeletion);
            return;
        }
        if (emergencyContacts.length > 1) {
            newContacts = emergencyContacts.slice();
            forDeletion = newContacts.splice(index, 1);
            if (forDeletion?.[0]?.clientEmergencyContactId) {
                setToBeDeleted([...toBeDeleted, ...forDeletion].filter(Boolean));
            }
            const newKeys = uniqueKeys.slice();
            newKeys.splice(index, 1);
            setUniqueKeys(newKeys);
            setEmergencyContacts(newContacts);
        }
    }, [emergencyContacts, uniqueKeys, contactDeclined, toBeDeleted])

    function derivePosition(index: number) {
        return (index && (emergencyContacts.length - 1) === index) ? "last" : index === 0 ? "first" : "middle"
    }

    function getResults() {
        const result = getActiveForms()
            .filter((form) => !isPristine(form?.values!))
            .map((form, i) => ({ ...emergencyContacts[i], ...(form?.values ?? {}) }));

        return contactDeclined ? result.map(res => ({ ...res, status: STATUS_TYPES.INACTIVE })) : result;
    }

    const containsChanges = useMemo(() =>{
        const originalContacts = activity?.client?.clientEmergencyContacts || [];
        if(originalContacts.length !== emergencyContacts.length) {
            return true;
        }
        const contacts = getResults();
        return contacts.some((contact, index) => {
            const [newContact, existing] = [JSON.stringify(contact, null, 2), JSON.stringify(omit(originalContacts[index], ['__typename']), null, 2)];
            return newContact !== existing;
        });
    }, [emergencyContacts, form1?.values, form2?.values, form3?.values]);

    const handleSubmit = useCallback(() => {
        if (!allValid() && contactDeclined !== true) {
            enqueueSnackbar(
                <div>{t('please_fix_all_errors')}
                    <ol>
                        {getActiveForms().map((f, i) => !isEmpty(f?.errors) && Object.values(f?.errors!).map(errMsg => <li>{t('contact')} {i}: {errMsg}</li>))}
                    </ol>
                </div>,
            { variant: "error" });
            return;
        }
        
        let contacts = getResults();
        if (toBeDeleted.length > 0) {
            contacts = contacts.concat(toBeDeleted.map(contact => ({...contact, status: STATUS_TYPES.INACTIVE})));
        }
        onSubmit({
            variables: {
                activityId: activity.activityId,
                clientId: activity.client.clientId,
                providerId: activity.providerId,
                contacts,
                emergencyContactDeclined: contactDeclined ?? false
            }
        });
    }, [toBeDeleted, contactDeclined, formikGetters]);

    const handleDeleteAll = (contacts: IEmergencyContact[]) => {
        onSubmit({
            variables: {
                activityId: activity.activityId,
                clientId: activity.client.clientId,
                providerId: activity.providerId,
                contacts,
                emergencyContactDeclined: true
            }
        });
    }

    function handleDecline(evt: ChangeEvent<HTMLInputElement>) {
        if (emergencyContacts?.length > 0 && !isPristine(formikGetters?.[emergencyContacts?.length - 1]?.values!) && evt.target.checked) {
            setDeleteIndex(-1);
        }
        setContactDeclined(evt.target.checked);
        if (evt.target.checked) {
            getActiveForms().forEach(form => form?.setErrors({}));
        }
    }
    
    function handleConfirm(evt: ChangeEvent<HTMLInputElement>) {
        setContactDeclined(!evt.target.checked);
    }

    function getCompleteButtonStatus() {
        if (readonly) {
            return !allValid() || (contactDeclined !== false && !containsChanges)
        } else {
            if (!allValid() && !contactDeclined) {
                return true;
            }
            if (contactDeclined) {
                return false;
            }
        }
    }

    return (
        <Grid container>
            {emergencyContacts?.map((emergencyContact, index) => (
                <EmergencyContactForm
                    key={emergencyContact.clientEmergencyContactId ?? uniqueKeys[index]}
                    title={`${t('emergency_contact')} ${labelSuffix[index]}`}
                    emergencyContact={emergencyContact}
                    position={derivePosition(index)}
                    onRemove={() => isPristine(formikGetters[index]?.values!) ? remove(index) : setDeleteIndex(index)}
                    setFormikContext={formikSetters[index]}
                    readonly={(!readonly && contactDeclined) || (!!emergencyContact.clientEmergencyContactId && (readonly || contactDeclined))}
                    hideActionButtons={activity?.client?.clientEmergencyContacts?.length === 0 || viewMode === 'tab'}
                    disabled={!readonly && contactDeclined}
                />
            ))}
            {(!readonly || emergencyContacts.length < MAX_CONTACTS) && (
                <Grid item xs={12} container textAlign={"right"}>
                    <Button variant="text" onClick={addMore} className='add_btn' disabled={!allValid() || emergencyContacts?.length >= MAX_CONTACTS}>
                        <span><IconAdd />{t('add_another_contact')}</span>
                    </Button>
                </Grid>
            )}
            {readonly ? (
                <Grid item xs={12}>
                        <FormControlLabel label={t('emergency_contact_info_is_up_to_date')} control={
                            <Checkbox
                                checked={contactDeclined === false && !containsChanges}
                                onChange={handleConfirm}
                                disabled={containsChanges}
                                name="emergencyContactConfirmed"
                                color="primary"
                            />
                            }
                        />
                </Grid>
            )
            :
            (
                <Grid item xs={12}>
                    <FormControlLabel label={t('client_did_not_provide_contact_info')} control={
                        <Checkbox
                            checked={contactDeclined === true}
                            onChange={handleDecline}
                            name="emergencyContactDeclined"
                            color="primary"
                        />
                        }
                    />
                </Grid>
            )}
            <Grid item xs={12} container spacing={2}>
                <Grid item>
                    {!readonly && (
                        <Button disabled={getCompleteButtonStatus()} type="submit" variant="opaque" onClick={handleSubmit as MouseEventHandler} busy={busy}>
                            {t('complete')}
                        </Button>
                    )}
                    {readonly && (
                         <>
                            <Button disabled={getCompleteButtonStatus()} type="submit" variant="opaque" onClick={handleSubmit as MouseEventHandler}>
                                {t('complete')}
                            </Button>
                            {!!activity?.client?.clientEmergencyContacts?.length && (
                                <Button variant="outline" style={{ color: "var(--color_secondary)" }} onClick={() => setDeleteIndex(DELETE_ALL_INDEX)}>
                                    {t('delete_all_contacts')}
                                </Button>
                            )}
                        </>
                    )}
                    {!!onCancel && (
                        <Button variant="outline" onClick={onCancel}>{t('cancel')}</Button>
                    )}
                </Grid>
            </Grid>
            <DeleteContactModal
                open={deleteIndex !== null}
                btnAction={() => { remove(deleteIndex!); setDeleteIndex(null); }}
                onClose={() => { setDeleteIndex(null); if (contactDeclined) setContactDeclined(false) }}
                index={deleteIndex!}
            />
        </Grid>
    );
});
